// to easily copy and paste components
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import ExpensesRecordTitle from "./expensesTitle";
import ExpensesRecordForm from "./recordExpenseForm";
import ExpensesRecordTable from "./recordExpenseTable";
import {BllinkLogger} from "../../common/utils/bllink_loggers";
import { constants } from "../../common/constants/constants";
import {StyledPageContainer} from './expenses.styled';
import {useDispatch, useSelector} from 'react-redux';
import {hideLoader, showLoader} from '../../store/common/slice';
import {verifyLoggedIn} from '../../store/auth/actions';
import {getAllExpenseTypes, getAllKnownVendors, getExpenseDetails} from '../../store/expenses/actions';

export default function ExpensesEdit({match, history}) {
    const {cartID} = match.params;
    const {t} = useTranslation();
    const dispatch = useDispatch();
    document.title = t('titles.expenses_edit');

    const [topFormData, setTopFormData] = useState({});
    const [expenses, setExpenses] = useState([]);
    const expenseTypes = useSelector(state => state.expenses.expenseTypes);
    const vendors = useSelector(state => state.expenses.allKnownVendors);
    const expenseDetails = useSelector(state => state.expenses.expenseDetails);

    useEffect(() => {
        (async function () {
            dispatch(showLoader());
            await dispatch(verifyLoggedIn());

            if (!expenseTypes.length) {
                await dispatch(getAllExpenseTypes());
            }
            if (!expenseDetails.buildingID) {
                await dispatch(getExpenseDetails(cartID));
            }
            const {buildingID} = expenseDetails;
            if (!vendors.length && buildingID) {
                await dispatch(getAllKnownVendors(buildingID));
            }

            dispatch(hideLoader());
        })();
    }, [expenseTypes, expenseDetails, vendors]);

    useEffect(() => {
        if (vendors.length) {
            setTopFormData({
                firstDate: '',
                selectedVendor: vendors[0],
                frequency: {value: constants.APIContract.expenses.oneTime, label: t('expenses.one_time')},
                selectedPaymentMethod: {value: constants.managerPaymentsOptions.cheque, label: t('offline_payments.cheque')}
            })
        }
        if (expenseDetails.buildingID) {
            setExpenses(parseExpensesToClientTable(expenseDetails.Expenses, expenseTypes))
            BllinkLogger.info(`cartID ${cartID} expenses are`, expenseDetails);
        }
    }, [expenseTypes, vendors, expenseDetails]);

    const parseExpensesToClientTable = (expenses, expenseTypes) => {
        let clientExpenses = [];
        const serverExpenses = [...expenses] || [];
        for (const serverExpense of serverExpenses) {
            clientExpenses.push({
                errors: [],
                description: serverExpense[0].comment || '',
                amount: serverExpense[0].amount || '0',
                expenseTypes: {all: [...expenseTypes], expenseTypeID: serverExpense[0].expenseTypeID},
                expenseTypeID: serverExpense[0].expenseTypeID // for the server update, if will change
            })
        }

        return clientExpenses;
    }

    const changeTopFormData = (data, fieldName) => {
        BllinkLogger.info(`${fieldName} changed`, data);
        setTopFormData({
            ...topFormData,
            [fieldName]: data
        });
    }

    const changePaymentDate = date => {
        setTopFormData({
            ...topFormData,
            firstDate: date
        });
    }

    const onChangeExpenseType = (expenseType, rowNumber) => {
        BllinkLogger.info(`updateExpenseTypeState`, expenseType.value, rowNumber);
        let expensesCopy = cloneDeep(expenses);
        expensesCopy[rowNumber].expenseTypes.expenseTypeID = expenseType;
        expensesCopy[rowNumber].expenseTypeID = expenseType.value; // for the server
        BllinkLogger.info(`new expenses`, expensesCopy);
        setExpenses(expensesCopy)
    }

    const updateTableData = (rowIndex, columnId, value, error) => {
        setExpenses(old =>
            old.map((row, index) => {
                if (index === rowIndex) {
                    return {
                        ...old[rowIndex],
                        [columnId]: value,
                        errors: {
                            ...row.errors,
                            [columnId]: {
                                touched: true,
                                valid: !error,
                                error: error ? error : undefined
                            }
                        }
                    };
                }
                return row;
            })
        );
    }

    return (
        <StyledPageContainer>
            <div className={"expenses-report"}>
                <ExpensesRecordTitle
                    buildingID={expenseDetails.buildingID}
                    titleType='edit'
                />

                <ExpensesRecordForm
                    vendors={vendors}
                    formData={topFormData}
                    onFormDataChanged={changeTopFormData}
                    onDateChanged={changePaymentDate}
                />

                <ExpensesRecordTable
                    expenseRows={expenses || []}
                    onChangeExpenseType={onChangeExpenseType}
                    setTableData={updateTableData}
                    disableActions
                />
            </div>
        </StyledPageContainer>
    )
}

